import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { UserStatus, TripContent, getChatMessages, AiEditContext } from '../../services/api';
import ChatMessages, { ChatMessage } from '../ChatMessages';
import ChatComposer from './ChatComposer.tsx';
import './ChatPanel.css';

const LockIcon = () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <rect width="18" height="11" x="3" y="11" rx="2" ry="2"/>
        <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
    </svg>
);

const UnlockIcon = () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <rect width="18" height="11" x="3" y="11" rx="2" ry="2"/>
        <path d="M7 11V7a5 5 0 0 1 9.9-1"/>
    </svg>
);

interface ChatPanelProps {
    tripSlug: string;
    userStatus: UserStatus | null;
    isTemplate: boolean;
    onShowUnlockModal: () => void;
    onUpdateTripData: (data: Partial<{ content: TripContent; versionNo: number }>) => void;
    onUserStatusUpdate: (status: UserStatus) => void;
    onClone: () => void;
    isOpenMobile?: boolean;
    onCloseMobile?: () => void;
    /** When user is on a newly added empty day, pass context so AI can suggest day n-1 updates (e.g. accommodation). */
    editContext?: AiEditContext;
}

/**
 * ChatPanel - Orchestrates the AI Travel Assistant interface.
 */
export default function ChatPanel({
    tripSlug,
    userStatus,
    isTemplate,
    onShowUnlockModal,
    onUpdateTripData,
    onUserStatusUpdate,
    onClone,
    isOpenMobile = false,
    onCloseMobile,
    editContext,
}: ChatPanelProps) {
    const { t } = useTranslation();
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    // Fetch messages on mount (only for working copies)
    useEffect(() => {
        if (userStatus?.isAuthenticated && !isTemplate) {
            getChatMessages(tripSlug).then((apiMessages) => {
                const mapped: ChatMessage[] = apiMessages.map((m) => ({
                    id: m.id,
                    role: m.role as 'user' | 'assistant',
                    content: m.content,
                    timestamp: new Date(m.createdAt),
                    patchType: m.patchType || undefined,
                }));
                setMessages(mapped);
            }).catch(() => {
                // Silently fail history
            });
        }
    }, [tripSlug, userStatus?.isAuthenticated, isTemplate]);

    // Scroll to bottom
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const addMessage = (msg: ChatMessage) => {
        setMessages(prev => [...prev, msg]);
    };

    return (
        <aside className={`chat-sidebar ${isOpenMobile ? 'mobile-open' : ''}`}>
            <div className="chat-header">
                <div className="chat-avatar">
                    <img src="/raisa.png" alt="Raisa" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </div>
                <div className="chat-header-text">
                    <h3>{t('chat.raisa_assistant', 'Raisa')}</h3>
                    {/* <span className="chat-subtitle">{t('chat.assistant_subtitle')}</span> */}
                </div>

                {isOpenMobile && (
                    <button className="close-chat-mobile" onClick={onCloseMobile} aria-label={t('common.close')}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                )}

                {isTemplate && <span className="chat-mode-tag">{t('chat.preview')}</span>}
                {/* Show status badge for non-premium authenticated users */}
                {!isTemplate && userStatus?.isAuthenticated && !userStatus.isPremium && (
                    <>
                        {userStatus.isTripUnlocked ? (
                            <span className="edits-badge unlocked">
                                <UnlockIcon /> {t('chat.unlocked')}
                            </span>
                        ) : (userStatus.freeEditsRemaining ?? 0) > 0 ? (
                            <span className="edits-badge">
                                {t('chat.edits_left', { count: userStatus.freeEditsRemaining })}
                            </span>
                        ) : (
                            <span className="edits-badge locked">
                                <LockIcon /> {t('chat.locked')}
                            </span>
                        )}
                    </>
                )}
            </div>

            <ChatMessages messages={messages} isLoading={isSubmitting} />
            <div ref={messagesEndRef} />

            <ChatComposer
                tripSlug={tripSlug}
                userStatus={userStatus}
                isTemplate={isTemplate}
                onShowUnlockModal={onShowUnlockModal}
                onTripUpdated={(content, versionNo) => onUpdateTripData({ content, versionNo })}
                onUserStatusUpdate={onUserStatusUpdate}
                onClone={onClone}
                setIsSubmitting={setIsSubmitting}
                isSubmitting={isSubmitting}
                addMessage={addMessage}
                editContext={editContext}
            />
        </aside>
    );
}
